import { useCallback } from "react";

import { EntityType } from "constants/index";
import { useModals } from "hooks";
import { useDealActions } from "store";
import { IFile, IFileMetaDetail, IMetaDetails } from "store/types";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";

interface UseBulkActionsHookParams {
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;
}

const isFolder = (item: IMetaDetails): boolean => item.type === EntityType.FOLDER;

export const useBulkActionsHook = ({ selectedItems, setSelectedItems }: UseBulkActionsHookParams) => {
  const { deleteModal } = useModals();
  const { downloadFileAction, deleteFileAction, deleteFolderAction } = useDealActions();
  const { removeFiles, refetchFolderMetadata } = useClientWorkspaceCtx();

  const handleBulkDelete = useCallback(() => {
    if (!selectedItems.length) return;

    const count = selectedItems.length;
    deleteModal({
      message: `Are you sure you want to delete ${count} selected ${count === 1 ? "item" : "items"}?`,
      action: async () => {
        const itemsToDelete = [...selectedItems];
        setSelectedItems([]);
        const folders = itemsToDelete.filter(isFolder);
        const files = itemsToDelete.filter((item): item is IFileMetaDetail => !isFolder(item));

        const outcomes = await Promise.allSettled(
          files.map((item) => deleteFileAction({ id: item.id, name: item.name, deal_id: null } as unknown as IFile))
        );
        const deletedIds = files.filter((_, i) => outcomes[i].status === "fulfilled").map((item) => item.id);
        removeFiles(deletedIds);

        if (!folders.length) return;
        await Promise.allSettled(folders.map((folder) => deleteFolderAction({ dealId: 0, folderId: folder.id })));
        await refetchFolderMetadata();
      }
    });
  }, [
    selectedItems,
    deleteModal,
    deleteFileAction,
    deleteFolderAction,
    removeFiles,
    refetchFolderMetadata,
    setSelectedItems
  ]);

  const handleBulkDownload = useCallback(async () => {
    for (const item of selectedItems) {
      if (isFolder(item)) continue;
      await downloadFileAction({ fileId: item.id, fileName: (item as IFileMetaDetail).name });
    }
  }, [selectedItems, downloadFileAction]);

  return { handleBulkDelete, handleBulkDownload };
};
